import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from 'antd';
import { useAuth } from '@clerk/clerk-react';

import { AdminService, type PaginationMeta } from '@/services/adminService';

export interface UseResultsTableDataReturn<TRow> {
  rows: TRow[];
  totalCount: number;
  loading: boolean;
}

/**
 * Fetch/pagination-race logic shared by both Results list panels: filters can
 * change faster than a response arrives, so only the latest request's result
 * may land, and a page the server clamped (e.g. a stale URL after rows were
 * filtered away) is followed to the last real page instead of showing nothing.
 */
export function useResultsTableData<TRow, TQuery>(
  fetchPage: (service: AdminService, page: number, filters: TQuery) => Promise<{ data: TRow[]; pagination: PaginationMeta }>,
  apiFilters: TQuery,
  page: number,
  setPage: (page: number) => void,
  listLabel: string,
): UseResultsTableDataReturn<TRow> {
  const { getToken } = useAuth();
  const { notification } = App.useApp();

  const [rows, setRows] = useState<TRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const latestRequestRef = useRef(0);

  const load = useCallback(
    async (requestedPage: number, requestFilters: TQuery) => {
      const requestId = ++latestRequestRef.current;
      setLoading(true);
      try {
        const token = await getToken();
        if (!token || requestId !== latestRequestRef.current) return;
        const result = await fetchPage(new AdminService(token), requestedPage, requestFilters);
        if (requestId !== latestRequestRef.current) return;
        if (result.pagination.page !== requestedPage) {
          setPage(result.pagination.page);
          return;
        }
        setRows(result.data);
        setTotalCount(result.pagination.totalCount);
      } catch (e) {
        if (requestId !== latestRequestRef.current) return;
        console.error(e);
        notification.error({ message: `Failed to load ${listLabel}` });
      } finally {
        if (requestId === latestRequestRef.current) setLoading(false);
      }
    },
    [fetchPage, getToken, listLabel, notification, setPage],
  );

  useEffect(() => {
    void load(page, apiFilters);
  }, [load, page, apiFilters]);

  return { rows, totalCount, loading };
}
